import React, { useEffect, useState, useMemo } from "react";
import _ from "lodash";
import PropTypes from "prop-types";
import classNames from "classnames";
import Table from "react-bootstrap/Table";
import { EventEmitter } from "events";

import Header from "./Header";
import Body from "./Body";
import { checkboxColumn } from "./model";
import Footer from "./Footer";
import { TablePropTypes } from "../propTypes";

const DEFAULT_ROW_ID_FIELD_NAME = "id";

const SELECT = "select";
const MULTI_SELECT = "multiSelect";
const REMOVE = "remove";
const CLEAR_SELECTED = "clearSelected";
const SET_ROWS = "setRows";

class SelectionManager extends EventEmitter {
  constructor(props) {
    super();
    this.rows = props?.rows ? [...props.rows] : [];
    this.rowIdFieldName = props?.rowIdFieldName || DEFAULT_ROW_ID_FIELD_NAME;
  }

  setRows = rows => {
    this.rows = [...rows];
    this.emit(SET_ROWS, rows);
  };

  select = (row, force = false) => {
    force
      ? this.setRows([row])
      : !this.rows.find(
          item => item[this.rowIdFieldName] === row[this.rowIdFieldName]
        ) && this.setRows([...this.rows, row]);

    this.emit(SELECT, row);
  };

  remove = row => {
    this.setRows(
      this.rows.filter(
        item => item[this.rowIdFieldName] !== row[this.rowIdFieldName]
      )
    );
    this.emit(REMOVE, row);
  };

  multiSelect = (rows, force = false) => {
    this.setRows(force ? [...rows] : [...this.rows, ...rows]);
    this.emit(MULTI_SELECT, rows);
  };

  clearSelected = () => {
    this.setRows([]);
    this.emit(CLEAR_SELECTED);
  };

  isChecked = row =>
    !!this?.rows.find(
      el => el[this.rowIdFieldName] === row[this.rowIdFieldName]
    );

  toggleSelection = row =>
    this.isChecked(row) ? this.remove(row) : this.select(row);
}

const BsTable = React.forwardRef(
  (
    {
      data,
      rowComponent,
      onClickRow,
      onDoubleClickRow,
      onSelectRow,
      onSelectAll,
      onSortColumn,
      columns,
      selectable,
      rowId: rowIdFieldName = DEFAULT_ROW_ID_FIELD_NAME,
      initialSelectedRows,
      applyRowStyles,
      tableContainerClassName,
      tableClassName,
      selectionManager: outerSelectionManager,
      children,
      ...restProps
    },
    ref
  ) => {
    const [sortedData, setSortedData] = useState(data);
    const [activeRow, setActiveRow] = useState({});
    const [sortColumn, setSortColumn] = useState({});
    const [shouldRerender, setShouldRerender] = useState(false);

    const selectionManager = useMemo(() => {
      const selectedRows = data.filter(
        el => initialSelectedRows.indexOf(el[rowIdFieldName]) !== -1
      );
      return (
        outerSelectionManager ||
        new SelectionManager({ selectedRows, rowIdFieldName })
      );
    }, []);

    useEffect(() => {
      shouldRerender && setShouldRerender(false);
    }, [shouldRerender]);

    useEffect(() => {
      selectionManager.on(SELECT, row => {
        onSelectRow &&
          onSelectRow(row, row[rowIdFieldName], selectionManager.rows);
      });
      selectionManager.on(MULTI_SELECT, rows => {
        onSelectAll && onSelectAll(rows);
      });
      selectionManager.on(SET_ROWS, rows => {
        setShouldRerender(true);
      });

      return () => {
        selectionManager.removeAllListeners();
      };
    }, []);

    const onSelectRowHandler = row => () =>
      selectionManager.toggleSelection(row);

    useEffect(() => {
      selectionManager.rows.length > 0 && selectionManager.setRows([]);
    }, [data]);

    const isSelectedAll = React.useMemo(
      () => selectionManager.rows.length === data.length && data.length > 0,
      [selectionManager.rows, data]
    );

    const onSelectAllHandler = () => {
      const selectedRows = !isSelectedAll ? data : [];
      selectionManager.multiSelect(selectedRows, true);
    };

    useEffect(() => {
      if (ref && ref.current) {
        ref.current.setActiveRow = setActiveRow;
      }
    }, [ref]);

    const onClickRowHandler = row => e => {
      setActiveRow(
        activeRow[rowIdFieldName] !== row[rowIdFieldName] &&
          !selectionManager.isChecked(row)
          ? row
          : {}
      );
      if (e.target.type !== "checkbox") {
        onClickRow(e, row);
      }
    };

    const onDoubleClickRowHandler = row => e => onDoubleClickRow(e, row);

    useEffect(() => {
      setSortedData(data);
    }, [data]);

    const defaultSorter = dataIndex => _.sortBy(data, [dataIndex]);

    const onSortHandler = (index, sorter) => () => {
      const order = sortColumn.column === index ? !sortColumn.order : true;
      setSortColumn({ column: index, order });
      const sortFunc = sorter || defaultSorter;
      const sorted = sortFunc(index, order, data) || data;
      setSortedData(order ? sorted : sorted.reverse());
      onSortColumn(index, order);
    };

    const tableColumns = React.useMemo(
      () => (selectable ? [checkboxColumn, ...columns] : columns),
      [columns, selectable]
    );

    return (
      <div
        className={classNames("mydpd-table-container", tableContainerClassName)}
      >
        <Table
          responsive="sm"
          className={classNames("mydpd-table", tableClassName)}
          ref={ref}
          {...restProps}
          borderless
        >
          <Header
            columns={tableColumns}
            onSortHandler={onSortHandler}
            onSelectAllHandler={onSelectAllHandler}
            sortColumn={sortColumn}
            selectedAll={isSelectedAll}
          />
          <Body
            data={sortedData}
            rowComponent={rowComponent}
            columns={tableColumns}
            selectable={selectable}
            activeRow={activeRow}
            isChecked={selectionManager.isChecked}
            applyRowStyles={applyRowStyles}
            rowId={rowIdFieldName}
            onClickRowHandler={onClickRowHandler}
            onDoubleClickRowHandler={onDoubleClickRowHandler}
            onSelectRowHandler={onSelectRowHandler}
          />
        </Table>
        {children && <Footer>{children}</Footer>}
      </div>
    );
  }
);

export const CellPropsTypes = {
  row: PropTypes.object,
  rowIndex: PropTypes.number,
};

BsTable.propTypes = TablePropTypes;

BsTable.defaultProps = {
  data: [],
  columns: [],
  onClickRow: () => null,
  onDoubleClickRow: () => null,
  selectable: false,
  initialSelectedRows: [],
  rowId: DEFAULT_ROW_ID_FIELD_NAME,
};

export default BsTable;
export { SelectionManager };
